import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { FiPhone, FiMapPin, FiSearch, FiCheckCircle } from 'react-icons/fi';
import RelatedInventory from '../../../components/RelatedInventory';

const VehicleDiagnostics = () => {
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="min-h-screen">
      <Helmet>
        <title>Auto Diagnostics Dallas | Check Engine Light Experts | Paradise-Performance</title>
        <meta name="description" content="Dallas car diagnostic specialists. Check engine lights, ABS, airbag, transmission codes. Factory-level scanning. $149 diagnostic fee waived with repair. Call (817) 382-2316." />
        <link rel="canonical" href="https://paradise-performance.com/services/vehicle-diagnostics" />
      </Helmet>

      {/* Hero */}
      <section className="relative py-20 bg-gradient-to-br from-teal-600 to-teal-900 text-white">
        <div className="container mx-auto px-4">
          <h1 className="text-5xl font-bold mb-6">Vehicle Diagnostics Dallas</h1>
          <p className="text-xl mb-8 max-w-3xl">
            That check engine light could be a $50 gas cap or a $5,000 engine problem. 
            The difference? A shop with real diagnostic tools, not a $30 code reader from AutoZone.
          </p>
          <a href="tel:469-928-8011" className="inline-block bg-white text-teal-600 px-8 py-4 rounded-lg font-bold hover:bg-gray-100">
            Get Real Answers: (817) 382-2316
          </a>
        </div>
      </section>

      {/* The Problem */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 max-w-4xl">
          <h2 className="text-3xl font-bold mb-8">Codes Don't Fix Cars, Diagnostics Do</h2>
          <p className="text-lg text-gray-700 mb-6">
            P0420? That's "catalyst efficiency." Could be a bad O2 sensor ($200), 
            clogged cat ($1,500), or exhaust leak ($100). Most shops? They'll sell 
            you the expensive catalytic converter first. We actually test components.
          </p>
          <div className="bg-yellow-50 p-6 rounded-lg border-l-4 border-yellow-500">
            <p className="font-semibold mb-2">Real example from yesterday:</p>
            <p className="text-gray-700">
              2017 Camry, P0301 misfire code. Other shop quoted spark plugs, coils, 
              and injector cleaning - $800. Actual problem? Torn vacuum line. $35 fix. 
              That's what proper diagnostics saves you.
            </p>
          </div>
        </div>
      </section>

      {/* What We Scan */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4 max-w-5xl">
          <h2 className="text-3xl font-bold mb-8 text-center">Complete System Diagnostics</h2>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-white p-6 rounded-lg shadow">
              <FiSearch className="w-12 h-12 text-teal-600 mb-4" />
              <h3 className="font-bold text-lg mb-2">Engine & Emissions</h3>
              <p className="text-gray-600">Check engine lights, misfires, fuel system, O2 sensors, catalytic converter efficiency</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow">
              <FiSearch className="w-12 h-12 text-teal-600 mb-4" />
              <h3 className="font-bold text-lg mb-2">Transmission & Drivetrain</h3>
              <p className="text-gray-600">Shift problems, slipping, TCM codes, speed sensors, solenoid issues</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow">
              <FiSearch className="w-12 h-12 text-teal-600 mb-4" />
              <h3 className="font-bold text-lg mb-2">ABS & Safety Systems</h3>
              <p className="text-gray-600">ABS lights, traction control, stability control, airbag systems</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow">
              <FiSearch className="w-12 h-12 text-teal-600 mb-4" />
              <h3 className="font-bold text-lg mb-2">Electrical Systems</h3>
              <p className="text-gray-600">Battery drain, charging issues, module communication errors</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow">
              <FiSearch className="w-12 h-12 text-teal-600 mb-4" />
              <h3 className="font-bold text-lg mb-2">Climate Control</h3>
              <p className="text-gray-600">A/C performance, heating issues, blend door problems</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow">
              <FiSearch className="w-12 h-12 text-teal-600 mb-4" />
              <h3 className="font-bold text-lg mb-2">Diesel Systems</h3>
              <p className="text-gray-600">DPF, DEF, turbo boost, glow plugs, injection timing</p>
            </div>
          </div>
        </div>
      </section>

      {/* Our Process */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 max-w-4xl">
          <h2 className="text-3xl font-bold mb-8">Our Diagnostic Process (The Right Way)</h2>
          <div className="space-y-4">
            <div className="flex items-start">
              <span className="bg-teal-600 text-white rounded-full w-8 h-8 flex items-center justify-center mr-4 flex-shrink-0">1</span>
              <div>
                <h3 className="font-bold mb-1">Scan ALL Modules</h3>
                <p className="text-gray-700">Not just engine codes. We check every computer in your car.</p>
              </div>
            </div>
            <div className="flex items-start">
              <span className="bg-teal-600 text-white rounded-full w-8 h-8 flex items-center justify-center mr-4 flex-shrink-0">2</span>
              <div>
                <h3 className="font-bold mb-1">Test Don't Guess</h3>
                <p className="text-gray-700">We verify with multimeters, scopes, and smoke machines. Codes are clues, not answers.</p>
              </div>
            </div>
            <div className="flex items-start">
              <span className="bg-teal-600 text-white rounded-full w-8 h-8 flex items-center justify-center mr-4 flex-shrink-0">3</span>
              <div>
                <h3 className="font-bold mb-1">Show You The Problem</h3>
                <p className="text-gray-700">We explain what's wrong in plain English, show you the failed parts.</p>
              </div>
            </div>
            <div className="flex items-start">
              <span className="bg-teal-600 text-white rounded-full w-8 h-8 flex items-center justify-center mr-4 flex-shrink-0">4</span>
              <div>
                <h3 className="font-bold mb-1">Fix It Right</h3>
                <p className="text-gray-700">Address root cause, not symptoms. Clear codes and verify the repair.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4 max-w-4xl text-center">
          <h2 className="text-3xl font-bold mb-8">Simple, Honest Pricing</h2>
          <div className="bg-white p-8 rounded-lg shadow-lg inline-block">
            <p className="text-5xl font-bold text-teal-600 mb-2">$149</p>
            <p className="text-xl mb-4">Complete Diagnostic Scan</p>
            <p className="text-gray-600 mb-4">Includes all modules, test drive, written report</p>
            <p className="text-green-600 font-bold text-lg">Fee waived if you approve repairs</p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-teal-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-4">Stop Guessing, Start Knowing</h2>
          <p className="text-xl mb-8">Don't throw parts at the problem. Get real diagnostics.</p>
          <a href="tel:469-928-8011" className="inline-block bg-white text-teal-600 px-10 py-5 rounded-lg font-bold text-lg hover:bg-gray-100">
            <FiPhone className="inline mr-2" /> (817) 382-2316
          </a>
          <p className="mt-6"><FiMapPin className="inline mr-2" />305 Co Rd 3470
Paradise, TX 76073</p>
        </div>
      </section>
      {/* Vehicle Inventory Links */}
      <div className="max-w-7xl mx-auto px-4 pb-12">
        <RelatedInventory limit={4} title="Vehicles For Sale" />
      </div>
    </motion.div>

  );
};

export default VehicleDiagnostics;
